import type { ChatTriggerSource } from '@cotera/api';

/**
 * Note: `webhook`, `cron`, and `event` never appear on a chat today — those
 * triggers dispatch a workflow run, and the agent node stamps the resulting chat
 * with `workflow`. Filtering by them therefore always returns nothing.
 */
export const CHAT_TRIGGER_SOURCES: ChatTriggerSource[] = [
  'chat',
  'chat-bot',
  'slack',
  'linear',
  'webhook',
  'warehouse',
  'cron',
  'event',
  'dataset',
  'compaction-summary',
  'workflow',
  'system',
  'api',
];

export const CHAT_TRIGGER_SOURCE_LABELS: Record<ChatTriggerSource, string> = {
  chat: 'Chat',
  'chat-bot': 'Chat bot',
  slack: 'Slack',
  linear: 'Linear',
  webhook: 'Webhook',
  warehouse: 'Warehouse',
  cron: 'Cron',
  event: 'Event',
  dataset: 'Dataset',
  'compaction-summary': 'Compaction',
  workflow: 'Workflow',
  system: 'System',
  api: 'API',
};

/**
 * Default filter for the standalone chat history list (user-initiated chats
 * only). Re-exported from `@cotera/api` rather than declared here: the server
 * counts a folder's surfaced chats by the same list, and the two must not drift.
 */
export { DEFAULT_CHAT_HISTORY_TRIGGER_SOURCES } from '@cotera/api';

/** Agent chats tab: an empty selection means "no filter", i.e. every source. */
export const resolveAgentTriggerSources = (
  selected: ChatTriggerSource[]
): ChatTriggerSource[] =>
  selected.length > 0 ? selected : [...CHAT_TRIGGER_SOURCES];

/**
 * Coarse buckets the chat history list filters by. Individual trigger sources
 * are too granular for the list toolbar, so they roll up into three groups.
 * `compaction-summary` and `system` are deliberately in no group — they are
 * internal artifacts, not conversations someone would browse, so no filter
 * selection can surface them.
 */
export type ChatTriggerGroup = 'chat' | 'workflow' | 'automation';

export const CHAT_TRIGGER_GROUPS: ChatTriggerGroup[] = [
  'chat',
  'workflow',
  'automation',
];

export const CHAT_TRIGGER_GROUP_LABELS: Record<ChatTriggerGroup, string> = {
  chat: 'Chats',
  workflow: 'Workflow runs',
  automation: 'Chat & automations',
};

export const CHAT_TRIGGER_GROUP_SOURCES: Record<
  ChatTriggerGroup,
  ChatTriggerSource[]
> = {
  chat: ['chat'],
  workflow: ['workflow'],
  automation: [
    'chat-bot',
    'slack',
    'linear',
    'webhook',
    'warehouse',
    'cron',
    'event',
    'dataset',
    'api',
  ],
};

/** Default group selection for the chat history list — user-initiated chats. */
export const DEFAULT_CHAT_TRIGGER_GROUPS: ChatTriggerGroup[] = ['chat'];

export const isChatTriggerGroup = (value: string): value is ChatTriggerGroup =>
  CHAT_TRIGGER_GROUPS.some((group) => group === value);

/**
 * Trigger sources to request for a group selection. An empty selection would
 * mean "no filter" to the API (which then hides integration triggers), so it
 * falls back to the default rather than silently changing meaning.
 */
export const triggerSourcesForGroups = (
  groups: ChatTriggerGroup[]
): ChatTriggerSource[] => {
  const selected = groups.length > 0 ? groups : DEFAULT_CHAT_TRIGGER_GROUPS;
  return selected.flatMap((group) => CHAT_TRIGGER_GROUP_SOURCES[group]);
};

export const formatChatTriggerSource = (
  source: ChatTriggerSource | null | undefined
): string => {
  if (source === undefined || source === null) {
    return CHAT_TRIGGER_SOURCE_LABELS.chat;
  }
  return CHAT_TRIGGER_SOURCE_LABELS[source];
};
